import styled from '@emotion/styled';
import { History, Location } from 'history';
import { MuiThemeProvider } from '@material-ui/core';
import { ClassAttributes, Component, createElement } from 'react';
import { IntlProvider } from 'react-intl';
import { Router } from 'react-router';
import { ScrollContext } from 'react-router-scroll-4';
import { JssProvider, jss } from '../customJss';
import theme from '../theme';
import Config from '../core/Config';
import Dependency from '../core/Dependency';
import State from '../core/State';
import { Provider as ConfigProvider } from './context/config';
import { Provider as DependencyProvider } from './context/dependency';
import { Provider as HistoryProvider } from './context/history';
import { Provider as StateProvider } from './context/state';
import { HEADBAR_HEIGHT } from './component/compound/HeadBar';
import ModalSpinner from './component/compound/ModalSpinner';
import DocumentTitle from './connected/DocumentTitle';
import _FatalErrorScreen from './connected/FatalErrorScreen';
import _FormattedSnackbar from './connected/FormattedSnackbar';
import _HeadBar from './connected/HeadBar';
import _NonFatalErrorSnackbars from './connected/NonFatalErrorSnackbars';
import _SignedInScreen from './connected/SignedInScreen';
import _SignedOutScreen from './connected/SignedOutScreen';
import GlobalStyle from '../GlobalStyle';

interface Props {
  config: Config;
  dependency: Dependency;
  history: History;
  state: State;
}

class Root extends Component<ClassAttributes<HTMLElement> & Props, Record<string, unknown>> {
  componentDidMount() {
    this.props.dependency.authenticationUsecase.signInFromPreviousSession();
  }

  componentDidCatch(err: Error) {
    this.props.dependency.errorReporter.reportError(err);
    this.props.dependency.errorPresenter.showAsFatal(err);
  }

  render() {
    return (
      <ConfigProvider value={this.props.config}>
        <DependencyProvider value={this.props.dependency}>
          <HistoryProvider value={this.props.history}>
            <StateProvider value={this.props.state}>
              <GlobalStyle />

              <Router history={this.props.history}>
                <ScrollContext
                  shouldUpdateScroll={(_: any, { location }: { location: Location }) => {
                    return location.state && location.state.scroll === false ? false : true;
                  }}
                >
                  <JssProvider jss={jss}>
                    <MuiThemeProvider theme={theme}>
                      <IntlProvider locale={this.props.state.language} messages={this.props.state.translations}>
                        <RootElement
                          style={{
                            paddingTop:
                              !this.props.state.authentication.signedInUser || !!this.props.state.fatalError
                                ? 0
                                : HEADBAR_HEIGHT,
                          }}
                        >
                          <DocumentTitle />

                          <SomethingProcessing
                            visible={this.props.state.fullScreenSpinner.necessity >= 1}
                            loadingMessage={this.props.state.fullScreenSpinner.loadingMessage}
                          />

                          <NonFatalErrorSnackbars />

                          <FormattedSnackbar />

                          <HeadBar />

                          <FatalErrorScreen />

                          <SignedOutScreen />

                          <SignedInScreen user={this.props.state.authentication.signedInUser} />
                        </RootElement>
                      </IntlProvider>
                    </MuiThemeProvider>
                  </JssProvider>
                </ScrollContext>
              </Router>
            </StateProvider>
          </HistoryProvider>
        </DependencyProvider>
      </ConfigProvider>
    );
  }
}

const RootElement = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100vh',
});

const HeadBar = styled(_HeadBar)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: HEADBAR_HEIGHT,
});

const FatalErrorScreen = styled(_FatalErrorScreen)({
  width: '100%',
  height: '100%',
});

const SignedInScreen = styled(_SignedInScreen)({
  width: '100%',
  height: '100%',
});

const SignedOutScreen = styled(_SignedOutScreen)({
  width: '100%',
  height: '100%',
});

const NonFatalErrorSnackbars = styled(_NonFatalErrorSnackbars)({
  zIndex: theme.zIndex.snackbar + 1,
});

const FormattedSnackbar = styled(_FormattedSnackbar)({
  zIndex: theme.zIndex.snackbar,
});

const SomethingProcessing = styled(ModalSpinner)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 10000 + 1,
});

export default Root;
